<script setup lang="ts">
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import DateTimeMaskInput from '@/components/createdUi/DateTimeMaskInput.vue'
import TimePicker from '@/components/createdUi/TimePicker.vue'
import UniversalTreeMultiSelect from '@/components/createdUi/UniversalTreeMultiSelect.vue'
import JournalCustomSelect from '@/modules/journals/components/JournalCustomSelect.vue'
import { formatDateTime, getFullName } from '@/utils/formatters'
import { useEntryForm } from '@/modules/journals/composables/entry/useEntryForm'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { ERouteNames } from '@/router/ERouteNames'

interface Props {
  isOpen: boolean
  journalId: number | null
  entryId?: number | null
  readOnly?: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'saved'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { width } = useDisplay()
const isSmallScreen = computed(() => width.value < 510)

const shareEntryTo = computed(() => {
  if (!props.journalId || !props.entryId) {
    return undefined
  }

  return {
    name: ERouteNames.JOURNAL_DETAIL,
    params: { id: props.journalId },
    query: { entry: String(props.entryId) },
  }
})

const {
  forceClose,
  requestClose,
  handleCloseConfirm,
  onDialogDismissAttempt,
  onDialogUpdate,
  showUnsavedChangesDialog,
  currentEntry,
  dateMenu,
  entryForm,
  errors,
  fileFields,
  getDateForPicker,
  getDateMaskValue,
  getDateTimeIsoValue,
  getFieldLabelBase,
  getFieldType,
  getFileNameFromPath,
  getFileUrl,
  getFormKey,
  getSelectDisplayValue,
  getSelectItems,
  getSingleSelectProp,
  getTimeMaskValue,
  getTreeModelValue,
  hasMoreChoices,
  isAttributeReadOnly,
  isFormReadOnly,
  isChoicesLoading,
  isFetchingAttributes,
  isMultiSelect,
  isSubmitting,
  journalAttributes,
  journalName,
  journalTreeStore,
  modalTitle,
  onDateInput,
  onDateSave,
  onDateTimeInput,
  onFieldChange,
  onFileChange,
  onSelectLoadMore,
  onSelectOpen,
  onSelectSearch,
  onSingleSelectChange,
  onTimeInput,
  onTimeSave,
  onTreeSelectChange,
  saveEntry,
  selectMenu,
  tempDate,
  tempTime,
  timeMenu,
  visibleAttributes,
} = useEntryForm(props, emit)
</script>

<template>
  <VDialog
    :model-value="isOpen"
    max-width="900"
    persistent
    @update:model-value="onDialogUpdate"
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard class="pa-6">
      <div class="d-flex mb-6">
        <div class="form_card d-flex align-center justify-space-between w-100">
          <div class="form_card__title flex-column align-start">
            <div class="d-flex align-center ga-2">
              <span class="text-h4">{{ modalTitle }}</span>
              <CopyEntityLink
                v-if="shareEntryTo"
                variant="inline"
                :to="shareEntryTo"
              />
            </div>
            <span
              v-if="journalName"
              class="text-body-2 text-medium-emphasis"
            >
              {{ journalName }}
            </span>
          </div>
          <CustomCloseButton :close-action="isFormReadOnly ? forceClose : requestClose" />
        </div>
      </div>

      <div class="d-flex flex-column gap-4">
        <div
          v-if="isFetchingAttributes"
          class="text-center pa-4"
        >
          <VProgressCircular indeterminate />
          <div class="mt-2">
            Загрузка атрибутов...
          </div>
        </div>

        <div
          v-else-if="journalAttributes.length === 0"
          class="text-center pa-4 text-medium-emphasis"
        >
          Нет атрибутов для этого журнала
        </div>

        <VForm
          v-else
          class="journal-form-fields"
          autocomplete="off"
          @submit.prevent="!isFormReadOnly && saveEntry()"
        >
          <VRow>
            <VCol
              v-for="attr in visibleAttributes"
              :key="attr.id"
              :cols="
                attr.attribute_type === 'boolean' ? 12 : attr.form_width || 6
              "
            >
              <div v-if="getFieldType(attr) === 'select'">
                <!-- Мультиселект -->
                <template v-if="isMultiSelect(attr)">
                  <VMenu
                    v-model="selectMenu[getFormKey(attr)]"
                    :close-on-content-click="false"
                    location="bottom start"
                    :disabled="isAttributeReadOnly(attr)"
                  >
                    <template #activator="{ props: menuProps }">
                      <VTextField
                        :model-value="getSelectDisplayValue(attr)"
                        :error-messages="errors[getFormKey(attr)]"
                        :loading="isChoicesLoading(attr)"
                        :disabled="isAttributeReadOnly(attr)"
                        variant="outlined"
                        readonly
                        hide-details="auto"
                        persistent-placeholder
                        :placeholder="`Выберите ${getFieldLabelBase(attr)}`"
                        append-inner-icon="ri-arrow-down-s-line"
                        class="cursor-pointer invisible-readonly"
                        v-bind="menuProps"
                        @click="onSelectOpen(attr)"
                      >
                        <template #label>
                          <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                        </template>
                      </VTextField>
                    </template>
                    <VCard min-width="320" max-width="480" style="height: 420px;">
                      <UniversalTreeMultiSelect
                        :field-key="getFormKey(attr)"
                        :store="journalTreeStore"
                        :model-value="getTreeModelValue(attr)"
                        placeholder="Поиск"
                        @update:model-value="onTreeSelectChange(attr, $event)"
                        @close="selectMenu[getFormKey(attr)] = false"
                      />
                    </VCard>
                  </VMenu>
                </template>

                <!-- Одиночный выбор с подгрузкой следующих страниц при скролле -->
                <template v-else>
                  <JournalCustomSelect
                    :label="getFieldLabelBase(attr)"
                    :placeholder="`Выберите ${getFieldLabelBase(attr)}`"
                    search-placeholder="Поиск"
                    :items="getSelectItems(attr)"
                    :model-value="getSingleSelectProp(attr).value?.id ?? null"
                    :required="attr.required"
                    :disabled="isAttributeReadOnly(attr)"
                    :error="!!errors[getFormKey(attr)]"
                    :loading="isChoicesLoading(attr)"
                    :has-more="hasMoreChoices(attr)"
                    @update:model-value="onSingleSelectChange(attr, $event ? { id: $event } : null)"
                    @search="onSelectSearch(attr, $event)"
                    @load-more="onSelectLoadMore(attr)"
                    @open="onSelectOpen(attr)"
                  />
                  <div
                    v-if="errors[getFormKey(attr)]"
                    class="text-caption text-error mt-1 ml-3"
                  >
                    {{ errors[getFormKey(attr)] }}
                  </div>
                </template>
              </div>

              <VTextField
                v-else-if="getFieldType(attr) === 'text'"
                :model-value="entryForm[getFormKey(attr)]"
                variant="outlined"
                persistent-placeholder
                hide-details="auto"
                :error-messages="errors[getFormKey(attr)]"
                :readonly="isAttributeReadOnly(attr)"
                autocomplete="off"
                @update:model-value="onFieldChange(getFormKey(attr), $event)"
              >
                <template #label>
                  <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                </template>
              </VTextField>

              <VTextField
                v-else-if="getFieldType(attr) === 'number'"
                :model-value="entryForm[getFormKey(attr)]"
                variant="outlined"
                persistent-placeholder
                hide-details="auto"
                :error-messages="errors[getFormKey(attr)]"
                :readonly="isAttributeReadOnly(attr)"
                type="number"
                autocomplete="off"
                @update:model-value="onFieldChange(getFormKey(attr), $event)"
              >
                <template #label>
                  <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                </template>
              </VTextField>

              <div
                v-else-if="getFieldType(attr) === 'date'"
                class="date-field"
              >
                <VMenu
                  v-model="dateMenu[getFormKey(attr)]"
                  :close-on-content-click="false"
                  :open-on-click="false"
                  location="bottom"
                  transition="scale-transition"
                >
                  <template #activator="{ props: menuProps }">
                    <VMaskInput
                      :disabled="isAttributeReadOnly(attr)"
                      :error-messages="errors[getFormKey(attr)]"
                      :model-value="getDateMaskValue(getFormKey(attr))"
                      class="invisible-readonly"
                      :required="attr.required"
                      mask="##.##.####"
                      return-masked-value
                      variant="outlined"
                      hide-details="auto"
                      autocomplete="off"
                      placeholder="дд.мм.гггг"
                      persistent-placeholder
                      v-bind="menuProps"
                      @update:model-value="(v: string) => onDateInput(getFormKey(attr), v)"
                    >
                      <template #label>
                        <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                      </template>

                      <template #append-inner>
                        <VIcon
                          v-if="!isAttributeReadOnly(attr)"
                          class="cursor-pointer"
                          icon="ri-calendar-line"
                          size="20"
                          @click.stop="
                            tempDate[getFormKey(attr)] = getDateForPicker(getFormKey(attr)) ?? new Date();
                            dateMenu[getFormKey(attr)] = !dateMenu[getFormKey(attr)]
                          "
                        />
                      </template>
                    </VMaskInput>
                  </template>

                  <VDatePicker
                    :model-value="tempDate[getFormKey(attr)]"
                    @update:model-value="(v: string | Date | null) => onDateSave(getFormKey(attr), v ? new Date(v) : null)"
                  />
                </VMenu>
              </div>

              <div
                v-else-if="getFieldType(attr) === 'date_time'"
                class="date-time-field"
              >
                <DateTimeMaskInput
                  :model-value="getDateTimeIsoValue(getFormKey(attr))"
                  :error-messages="errors[getFormKey(attr)]"
                  :disabled="isAttributeReadOnly(attr)"
                  @update:model-value="onDateTimeInput(getFormKey(attr), $event)"
                >
                  <template #label>
                    <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                  </template>
                </DateTimeMaskInput>
              </div>

              <div
                v-else-if="getFieldType(attr) === 'time'"
                class="time-field"
              >
                <VMenu
                  v-model="timeMenu[getFormKey(attr)]"
                  :close-on-content-click="false"
                  :open-on-click="false"
                  location="bottom"
                  transition="scale-transition"
                >
                  <template #activator="{ props: menuProps }">
                    <VMaskInput
                      :disabled="isAttributeReadOnly(attr)"
                      :error-messages="errors[getFormKey(attr)]"
                      :model-value="getTimeMaskValue(getFormKey(attr))"
                      class="invisible-readonly"
                      :required="attr.required"
                      mask="##:##"
                      return-masked-value
                      variant="outlined"
                      hide-details="auto"
                      autocomplete="off"
                      placeholder="чч:мм"
                      persistent-placeholder
                      v-bind="menuProps"
                      @update:model-value="(v: string) => onTimeInput(getFormKey(attr), v)"
                    >
                      <template #label>
                        <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                      </template>

                      <template #append-inner>
                        <VIcon
                          v-if="!isAttributeReadOnly(attr)"
                          class="cursor-pointer"
                          icon="ri-time-line"
                          size="20"
                          @click.stop="
                            timeMenu[getFormKey(attr)]
                              = !timeMenu[getFormKey(attr)]
                          "
                        />
                      </template>
                    </VMaskInput>
                  </template>

                  <TimePicker
                    v-if="!isAttributeReadOnly(attr)"
                    v-model="tempTime[getFormKey(attr)]"
                    :minute-step="5"
                    @save="onTimeSave(getFormKey(attr), $event)"
                  />
                </VMenu>
              </div>

              <VCheckbox
                v-else-if="getFieldType(attr) === 'checkbox'"
                :model-value="entryForm[getFormKey(attr)]"
                :error-messages="errors[getFormKey(attr)]"
                :disabled="isAttributeReadOnly(attr)"
                hide-details="auto"
                @update:model-value="onFieldChange(getFormKey(attr), $event)"
              >
                <template #label>
                  <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                </template>
              </VCheckbox>

              <div v-else-if="getFieldType(attr) === 'file'">
                <div
                  v-if="entryForm[getFormKey(attr)] && !fileFields[attr.id]"
                  class="mb-2"
                >
                  <VTextField
                    :model-value="
                      typeof entryForm[getFormKey(attr)] === 'string'
                        ? getFileNameFromPath(entryForm[getFormKey(attr)])
                        : 'Файл загружен'
                    "
                    readonly
                    variant="outlined"
                    persistent-placeholder
                    autocomplete="off"
                    prepend-inner-icon="ri-file-line"
                    hide-details="auto"
                  >
                    <template #label>
                      <span>Текущий файл</span>
                    </template>
                    <template #append-inner>
                      <VIcon
                        v-if="
                          typeof entryForm[getFormKey(attr)] === 'string'
                        "
                        icon="ri-download-line"
                        class="cursor-pointer"
                        @click="
                          window.open(
                            getFileUrl(entryForm[getFormKey(attr)]),
                            '_blank',
                          )
                        "
                      />
                    </template>
                  </VTextField>
                </div>

                <VFileInput
                  v-if="!isAttributeReadOnly(attr)"
                  :error-messages="errors[getFormKey(attr)]"
                  prepend-icon=""
                  prepend-inner-icon="ri-attachment-line"
                  variant="outlined"
                  persistent-placeholder
                  :show-size="true"
                  autocomplete="off"
                  hide-details="auto"
                  @change="onFileChange(attr.id, $event)"
                >
                  <template #label>
                    <span>
                      {{ entryForm[getFormKey(attr)] ? 'Заменить файл' : getFieldLabelBase(attr) }}
                      <sup v-if="attr.required && !entryForm[getFormKey(attr)]" class="required-label">*</sup>
                    </span>
                  </template>
                </VFileInput>
              </div>

              <VTextField
                v-else
                :model-value="entryForm[getFormKey(attr)]"
                variant="outlined"
                persistent-placeholder
                hide-details="auto"
                :error-messages="errors[getFormKey(attr)]"
                :readonly="isAttributeReadOnly(attr)"
                autocomplete="off"
                @update:model-value="onFieldChange(getFormKey(attr), $event)"
              >
                <template #label>
                  <span>{{ getFieldLabelBase(attr) }}<sup v-if="attr.required" class="required-label">*</sup></span>
                </template>
              </VTextField>
            </VCol>
          </VRow>
        </VForm>

        <div
          v-if="!isFormReadOnly"
          class="d-flex gap-4"
          :class="[
            isSmallScreen
              ? 'flex-column align-center'
              : 'justify-space-between align-center',
          ]"
        >
          <div
            v-if="currentEntry?.created_by || currentEntry?.created_at"
            class="d-flex flex-column gap-2 text-caption text-medium-emphasis"
            :class="[
              isSmallScreen ? 'align-center' : 'align-start',
            ]"
          >
            <div
              v-if="currentEntry?.created_by"
              class="d-flex align-center gap-1"
            >
              <VIcon
                icon="ri-user-3-line"
                size="16"
              />
              <span>Автор: {{ getFullName(currentEntry.created_by) }}</span>
            </div>
            <div
              v-if="currentEntry?.created_at"
              class="d-flex align-center gap-1"
            >
              <VIcon
                icon="ri-time-line"
                size="16"
              />
              <span>Создано: {{ formatDateTime(currentEntry.created_at) }}</span>
            </div>
          </div>
          <div v-else />
          <div
            class="d-flex gap-2"
            :class="[isSmallScreen ? 'w-100' : '']"
          >
            <VBtn
              variant="outlined"
              :disabled="isSubmitting"
              :class="isSmallScreen ? 'flex-1-1-50' : ''"
              @click="requestClose"
            >
              Отмена
            </VBtn>
            <VBtn
              color="primary"
              :loading="isSubmitting"
              :class="isSmallScreen ? 'flex-1-1-50' : ''"
              @click="saveEntry"
            >
              {{ props.entryId ? "Сохранить" : "Создать" }}
            </VBtn>
          </div>
        </div>
        <div
          v-else
          class="d-flex gap-2"
          :class="[
            isSmallScreen
              ? 'flex-column align-center'
              : 'justify-space-between align-center',
          ]"
        >
          <div
            v-if="currentEntry?.created_by || currentEntry?.created_at"
            class="d-flex flex-column gap-2 text-caption text-medium-emphasis"
            :class="[
              isSmallScreen ? 'align-center' : 'align-start',
            ]"
          >
            <div
              v-if="currentEntry?.created_by"
              class="d-flex align-center gap-1"
            >
              <VIcon
                icon="ri-user-3-line"
                size="16"
              />
              <span>Автор: {{ getFullName(currentEntry.created_by) }}</span>
            </div>
            <div
              v-if="currentEntry?.created_at"
              class="d-flex align-center gap-1"
            >
              <VIcon
                icon="ri-time-line"
                size="16"
              />
              <span>Создано: {{ formatDateTime(currentEntry.created_at) }}</span>
            </div>
          </div>
          <div v-else />
          <VBtn
            variant="outlined"
            :class="isSmallScreen ? 'w-100' : ''"
            @click="forceClose"
          >
            Закрыть
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Продолжить"
    @confirm="handleCloseConfirm"
  />
</template>

<style scoped lang="scss">
@use '@/modules/journals/styles/journalFormFields' as jf;

.journal-form-fields {
  @include jf.fields-height;
}

:deep(.v-field) {
  font-size: 0.875rem;
}

.form_card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.form_card__title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.required-label {
  color: rgb(var(--v-theme-error));
}

.cursor-pointer {
  cursor: pointer;
}

.date-time-field {
  width: 100%;
}

.time-field {
  width: 100%;
}

.flex-1-1-50 {
  flex: 1 1 50%;
}
</style>
